import React, { useState, useEffect } from 'react';
import {
  WORKSHOP_INTEREST_SURVEY,
  emptySurveyValues,
  pruneHiddenSurveyValues,
  validateInterestSurvey,
  interestSurveyToPayload,
  isFieldVisible,
  InterestSurveyValues,
  SurveyField,
} from '../../data/workshop2026InterestSurvey';
import styles from '../../styles/workshopInterestModal.module.css';

interface WorkshopInterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ReturnType<typeof interestSurveyToPayload>) => Promise<void>;
}

const WorkshopInterestModal: React.FC<WorkshopInterestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<InterestSurveyValues>(emptySurveyValues);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setValues(emptySurveyValues());
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const setFieldValue = (fieldId: string, value: string) => {
    setValues((prev) => pruneHiddenSurveyValues({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateInterestSurvey(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(interestSurveyToPayload(values));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register interest');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: SurveyField) => {
    if (!isFieldVisible(field, values)) return null;

    if (field.type === 'select') {
      return (
        <div key={field.id} className={styles.field}>
          <label className={styles.fieldLabel} htmlFor={field.id}>
            {field.label}
          </label>
          <select
            id={field.id}
            className={styles.select}
            value={values[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            required
          >
            <option value="">Select…</option>
            {field.options.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {field.helper && <p className={styles.helper}>{field.helper}</p>}
        </div>
      );
    }

    return (
      <div key={field.id} className={styles.field}>
        <span className={styles.fieldLabel}>{field.label}</span>
        <div className={styles.radioGroup}>
          {field.options.map(({ value, label }) => (
            <label key={value} className={styles.radioLabel}>
              <input
                type="radio"
                name={field.id}
                value={value}
                checked={values[field.id] === value}
                onChange={() => setFieldValue(field.id, value)}
              />
              {label}
            </label>
          ))}
        </div>
        {field.helper && <p className={styles.helper}>{field.helper}</p>}
      </div>
    );
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <div>
            <h2>Join interest list</h2>
            <p className={styles.subtitle}>
              A few questions so we can prepare for the 2026 SAA Workshop. Please answer all
              questions shown.
            </p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.body}>
            {WORKSHOP_INTEREST_SURVEY.map(renderField)}
            {error && <p className={styles.error}>{error}</p>}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Join interest list'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkshopInterestModal;
